import { DateTime } from 'luxon';

/*
 * Shared types + PLACEHOLDER data for the Home-tab live health (stats + bitrate chart).
 * Mirrors the backend Mux proxy we specced in docs/mux-ingest-health-spec.md
 * (ingest-health + mux-metrics). Swap `buildMockHealth` for the real hook later.
 */

export type MuxRange = '1h' | '15m' | 'full' | 'live';

/** Where the event sits in time; decides which Mux data (if any) we can show. */
export type EventPhase = 'live' | 'past' | 'upcoming';

/*
 * SWAP POINT: base URL for the Mux health endpoints. Points at the Netlify demo
 * functions today; change it to our own backend once those endpoints exist.
 * Set USE_MOCK to true to bypass the network and use buildMockHealth instead.
 */
export const MUX_BASE = 'https://command-center-redesign.netlify.app/.netlify/functions';
// Typed as boolean (not the `false` literal) so toggling it doesn't trip no-unnecessary-condition.
// eslint-disable-next-line @typescript-eslint/no-inferrable-types
export const USE_MOCK: boolean = false;

/** Spacing between ingest bitrate samples, in seconds (Mux resolution). */
export const SAMPLE_SECONDS = 6;

export interface BitratePoint {
  /** ISO timestamp of the sample. */
  time: string;
  /** Video ingest bitrate, in Kbps. */
  kbps: number;
}

export interface MuxHealth {
  online: boolean;
  /** Instantaneous video bitrate (Kbps). */
  videoKbps: number;
  audioKbps: number;
  fps: number;
  viewers: number;
  /** Mux quality score, 0–100. */
  qualityScore: number;
  dips: number;
  drops: number;
  /** Millis when ingest started (for uptime/duration). Absent if the source doesn't provide it. */
  streamStartMs?: number;
  /** Whole-event video-bitrate series, oldest → newest. */
  series: BitratePoint[];
}

// Deterministic jagged bitrate (~2500 Kbps) so the mock chart looks alive but stable.
const sampleKbps = (i: number): number => {
  const base = 2500;
  const swell = Math.sin(i / 22) * 180;
  const jitter = Math.sin(i * 1.9) * 70 + Math.sin(i * 0.7) * 45;

  return Math.round(base + swell + jitter);
};

const MOCK_EVENT_MINUTES = 47;

/*
 * PLACEHOLDER source. Returns the shape the backend Mux proxy should give us.
 * Only used while the real endpoints don't exist yet.
 */
export const buildMockHealth = (): MuxHealth => {
  const now = DateTime.now();
  const count = Math.round((MOCK_EVENT_MINUTES * 60) / SAMPLE_SECONDS);

  const series: BitratePoint[] = Array.from({ length: count }, (_element, index) => {
    const time = now.minus({ seconds: (count - 1 - index) * SAMPLE_SECONDS });

    return { time: time.toISO(), kbps: sampleKbps(index) };
  });

  const current = series[series.length - 1]?.kbps ?? 0;

  return {
    online: true,
    videoKbps: current,
    audioKbps: 160,
    fps: 30,
    viewers: 164,
    qualityScore: 87,
    dips: 0,
    drops: 0,
    streamStartMs: now.minus({ minutes: MOCK_EVENT_MINUTES }).toMillis(),
    series
  };
};

export interface BitrateStats {
  avg: number;
  peak: number;
  min: number;
}

export const bitrateStats = (series: BitratePoint[]): BitrateStats => {
  if (series.length === 0) {
    return { avg: 0, peak: 0, min: 0 };
  }

  const values = series.map((point) => point.kbps);
  const sum = values.reduce((acc, value) => acc + value, 0);

  return {
    avg: Math.round(sum / values.length),
    peak: Math.max(...values),
    min: Math.min(...values)
  };
};

/** A sample under this share of the average counts as a dip. */
const DIP_THRESHOLD = 0.7;

/*
 * How much of the window has to be degraded before the stream reads as shaky
 * (warn) or bad. Judged as a SHARE of the window, not as a count of incidents:
 * two 6-second dips are noise across 90 minutes and alarming across 5.
 */
const DEGRADED_WARN = 0.01;
const DEGRADED_BAD = 0.05;

export type BitrateHealth = 'bad' | 'good' | 'warn';

export interface BitrateIncidents {
  dips: number;
  drops: number;
  /** Share of the window spent below the dip threshold or at zero, 0–1. */
  degraded: number;
  /** Overall read of the window; drives the chart colour. */
  health: BitrateHealth;
}

/*
 * Dips and drops read off the ingest series, relative to its own average: a
 * sample below 70% of the average is a dip, and a sample at 0 is a drop.
 *
 * Counted as EPISODES, not samples: the series carries a point every ~6s, so
 * counting per sample would report a two-minute outage as twenty drops. Each
 * descent into a band counts once, and only when coming from a different band.
 */
export const bitrateIncidents = (series: BitratePoint[]): BitrateIncidents => {
  const { avg } = bitrateStats(series);

  if (avg <= 0) {
    return { dips: 0, drops: 0, degraded: 0, health: 'good' };
  }

  const floor = avg * DIP_THRESHOLD;
  let dips = 0;
  let drops = 0;
  let degradedSamples = 0;
  let previous: 'dip' | 'drop' | 'ok' = 'ok';

  series.forEach((point) => {
    let state: 'dip' | 'drop' | 'ok' = 'ok';

    if (point.kbps <= 0) {
      state = 'drop';
    } else if (point.kbps <= floor) {
      // Inclusive: at avg 1000 the dip band is 700 down to 1, 0 being a drop.
      state = 'dip';
    }

    if (state !== 'ok') {
      degradedSamples += 1;
    }

    if (state !== previous) {
      if (state === 'dip') {
        dips += 1;
      }

      if (state === 'drop') {
        drops += 1;
      }
    }

    previous = state;
  });

  const degraded = degradedSamples / series.length;
  let health: BitrateHealth = 'good';

  /*
   * A drop is its own class of failure — the stream was off air, however
   * briefly — so it goes straight to bad instead of being averaged away.
   */
  if (drops > 0 || degraded >= DEGRADED_BAD) {
    health = 'bad';
  } else if (degraded >= DEGRADED_WARN) {
    health = 'warn';
  }

  return { dips, drops, degraded, health };
};

// Formats a duration in seconds as H:MM:SS.
export const formatDuration = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const pad = (n: number): string => n.toString().padStart(2, '0');

  return `${hours}:${pad(minutes)}:${pad(secs)}`;
};

/*
 * Zeroed health for a past event Mux can no longer answer for: its ingest data
 * ages out after about a day. Deliberately NOT the mock -- a fabricated curve
 * on a finished event reads as a real recording of how it went.
 */
export const EMPTY_HEALTH: MuxHealth = {
  online: false,
  videoKbps: 0,
  audioKbps: 0,
  fps: 0,
  viewers: 0,
  qualityScore: 0,
  dips: 0,
  drops: 0,
  series: []
};
